define("tmds/layers/Layer",	[
	"jquery",
    "d3",
    "tmds/layers/utils",
    "tmds/renderers/Renderer",
	"tmds/SvgElement",
    "tmds/utils/rendererUtils",
	"tmds/utils/svgUtils",
    "tmds/utils/symbolUtils",
	"tmds/validator"
], function($, d3, lyrUtils, Renderer, SvgElement, rendererUtils, svgUtils, symUtils, validator){
	"use strict";
	
	var _counter = 0;
	
	/**
	 * Construct a new layer
	 * @param dataCollection {Array} Required. array of source data used to create the layer
	 * @param metadata {Object} Required. metadata object that contains geometry related info
	 * 	{ 
	 * 		geometryType: "Point" | "Line" | "Polyline" | "Extent",
	 * 		geometryInfo: 
	 * 			geometryType == "Point": { x: "xCoord", y: "yCoord" } 
	 * 			geometryType == "Line": {x1: "leftX", y1: "leftY", x2: "rightX", y2: "rightY"} 
	 * 			geometryType == "Polyline": { paths: "coords" }
	 * 	}
	 * @param options {Object} Optional. options object
	 * 	{ 
	 * 		id: "lyrDefects", 		// layer id
	 * 		opacity: 1,				// initial layer opacity. Value is in range of [0..1] 
	 * 		visible: true | false,	// initial layer visibility 
	 * 		renderer: {} 			// layer renderer in JSON representation
	 * 	}
	 * @returns {tmds.layers.Layer}
	 * @note without metadata, there is no way to decide what svg element will be created for the data
	 */
	var Layer = function(dataCollection, metadata, options) {
		
		this.id = "layer" + _counter++;
		this.opacity = 1;
		this.visible = true;
		this.renderer = rendererUtils.fromSpatialType(metadata.geometryType);
//		this.selectSymbol = symUtils.fromSpatialType(metadata.geometryType);
		
		if (options != undefined) {
			
			if(validator.isDataObject(options)) {
			
				if(options.id != undefined) {
					
					if(! validator.isString(options.id)) {
					
						throw new Error('Invalid id value - ' + options.id + ' passed');
					}
					this.id = options.id;
				}
				
				if(options.opacity != undefined)
					setOpacity.call(this, options.opacity);
				
				if(options.visible != undefined) {
					
					if(! validator.isBoolean(options.visible)) {
					
						throw new Error('Invalid visible value - ' + options.visible + ' passed');
					}
					this.visible = options.visible;
				}
				
				if(options.renderer != undefined) {
					
					if(! validator.isDataObject(options.renderer)) {
					
						throw new Error('Invalid or empty renderer object - ' + options.renderer + ' passed');
					}
					this.renderer = rendererUtils.fromJson(options.renderer);
				}
				
//				if(options.selectSymbol != undefined) {
//					
//					if(! validator.isDataObject(options.selectSymbol)) {
//					
//						throw new Error('Invalid or empty selectSymbol object - ' + options.selectSymbol + ' passed');
//					}
//					this.selectSymbol = symUtils.fromJson(options.selectSymbol);
//				}
			}
			else {
			 	throw new Error('Invalid options values - ' + options + ' passed');
		 	}
		}
		
		var me = this;
		this.elements = $.map(dataCollection, function(entity) {
			var svgElem = new SvgElement({
				geometry: lyrUtils.getGeometry(entity, metadata),
				symbol: lyrUtils.getSymbol(entity, metadata, options ? options.renderer : null),
				attributes: entity
			});
			svgElem.setLayer(me);
			
			return svgElem;
		});

		// layer will be marked as loaded ONLY when the SVG elements of the layer are created and loaded.
		// layer.loaded will be set to true in Schematic.addLayer method.
		this.loaded = false;
		
		// reference to the hosting schematic; will be set when the layer is added to the schematic
		this._schematic = null;
		
		// reference to the group svg node served as the container of all layer elements
		this._layerNode = null;
		
		// store all event handlers for elements
		this._elemHandlers = {};
	};
	
	/**
	 * 
	 */
	function loadData(data, metadata) {
		if (validator.isArray(data) && validator.isDataObject(metadata)) {
			data.forEach(function(entity) {
				var svgElem = new SvgElement({
					geometry: lyrUtils.getGeometry(entity, metadata),
					symbol: lyrUtils.getSymbol(entity, metadata, this.renderer),
					attributes: entity
				});
				
				this.addElement(svgElem);
				
			}, this);
		}
	}
	
	/**
	 * add a svg element to the layer
	 * @param svgElement {tmds.SvgElement} 
	 */
	function addElement(svgElement) {
		if (svgElement instanceof SvgElement) {
			svgElement.setLayer(this);
			
			if (this._layerNode) {
				svgElement.createNode();
			}
			
			this.elements.push(svgElement);
			
			var svgNode = svgElement.getNode();
			
			// apply the existing event handlers to the newly added element
			for (var evtType in this._elemHandlers) {
				// define a partial function to pass the handler over
				$(svgNode).on(evtType, $.proxy(function(handler, e) {
					e.stopPropagation();
					return handler($.extend(e, {svgElement: svgElement}));
				}, this, this._elemHandlers[evtType]));
			}
		}
		
		return this;
	}
	
	/**
	 * remove a svg element from the layer
	 * @param svgElement {tmds.SvgElement} 
	 */
	function removeElement(svgElement) {
		if (svgElement instanceof SvgElement) {
			// remove from this.elements and childNodes list
			var idxElem = this.elements.indexOf(svgElement);
			if (idxElem > -1) {
				this.elements.splice(idxElem, 1);
				getNode.call(this).removeChild(svgElement.getNode());
			}
		}
		
		return this;
	}

	/**
	 * display the layer
	 */
	function show() {
		this.visible = true;
		d3.select(this._layerNode).classed("hide", false);
	}

	/**
	 * hide the layer
	 */
	function hide() {
		this.visible = false;
		d3.select(this._layerNode).classed("hide", true);
	}
	
	/**
	 * get the schematic that hosts the layer
	 */
	function getSchematic() {
		return this._schematic;
	}
	
	/**
	 * set the schematic that hosts the layer
	 * @param schm {tmds.Schematic} schematic object
	 * @return the layer itself
	 */
	function setSchematic(schm) {
		this._schematic = schm;
		
		return this;
	}
	
	/**
	 * get the layer svg node
	 */
	function getNode() {
		return this._layerNode;
	}
	
	/**
	 * set the layer svg node
	 * @return the layer itself
	 */
	function setNode(node) {
		this._layerNode = node;
		
		return this;
	}
	
	/**
	 * set the layer renderer
	 * @param renderer {tmds.renderers.Renderer}
	 * @return the layer itself
	 * @thought
	 * The Layer class is kind of mix of ESRI GraphicsLayer and FeatureLayer, which seems a bit problematic.
	 * addElement() allows to add any type of svgElement to the layer, where
	 * setRenderer() will applies the same renderer to all elements. If there exists elements with diff 
	 * geometry types, it will fail to render those elements with diff geometry type than the one the new 
	 * renderer is for.
	 * 
	 * Given that, will hold on the implementation and disable it for now.
	 */
//		function setRenderer(renderer) {
//			if (renderer instanceof Renderer) {
//				this.renderer = renderer;
//			}
//			
//			// TODO: update the symbology based on the new renderer
//			
//			return this;
//		}
	
	/**
	 * redraw the layer with the schematic transformation
	 */
	function redraw() {
		var zoomer = this._schematic._zoomer,
			translate = zoomer.translate(),
			scale = zoomer.scale();
		var transform = "translate(" + translate[0] + "," + translate[1] + ") scale(" + scale + ")";
		
		d3.select(this._layerNode).attr("transform", transform);
	}

	/**
	 * set the layer opacity
	 * @param opacity {Number} range of [0..1]
	 * @return the layer itself
	 */
	function setOpacity(opacity) {
		
		if(! validator.isOpacity(opacity)) {
			
			throw new Error('Invalid opacity value - ' + opacity + ' passed');
		}
		this.opacity = Number(opacity);
		
		// set the opacity of the layer
		d3.select(this._layerNode).attr("opacity", opacity);
		
		return this;
	}

	/**
	 * clear all elements within the layer
	 */
	function clear() {
		this.elements = [];
		d3.select(this._layerNode).selectAll("*").remove();
	}
	
	/**
	 * select elements 
	 * @param query {Object} query object. Currently only supports
	 * 		{ geometry: ext }  // ext: {tmds.geometry.Extent}
	 * @param funcSuccess {Function} callback function when the query succeeds
	 * @note Currently, the implementation only supports query by extent. May support query by other 
	 * 	geometry type and by attributes when needed.
	 */
	function selectElements(query, funcSuccess) {
		var elems = [];
		this.elements.forEach(function(elem) {
			if (query.geometry.intersects(elem.geometry)) {
				elems.push(elem);
			}
		});
		
		if (funcSuccess) {
			funcSuccess(elems);
		}
	}
	
	/**
	 * event handlers
	 */
	
	/**
	 * helper function to wrap jQury trigger to trigger events
	 */
	function trigger(evtType, params) {
		$(this).trigger(evtType, params);
	}
	
	/**
	 * enable to define event handler of the layer
	 * @param evtType {String} event type
	 * @param callback {Function} callback function
	 */
	function on(evtType, callback) {
		switch (evtType) {
			case "click":
			case "contextmenu":				
			case "dblclick":
			case "hover":
			case "mousedown":
			case "mouseenter":
			case "mouseleave":
			case "mousemove":
			case "mouseout":
			case "mouseover":
			case "mouseup":
			case "toggle":
				_onMouseEvent.call(this, evtType, callback);
				break;
			case "load":
				_onLoad.call(this, callback);
				break;
			default:
				break;
		}
	}
	
	/**
	 * layer onload event
	 */
	function _onLoad(callback) {
		$(this).on("load", callback);
	}
	
	/**
	 * layer mouse event handler
	 * triggered when any mouse event jQuery supports occurs on one of the layer svg elements
	 * @param callback {Function} 
	 */ 
	function _onMouseEvent(evtType, callback) {
		var me = this;
		
		$.each(this.elements, function(_, elem) {
			var svgNode = elem.getNode();
			
			if (svgNode) {
				$(svgNode).on(evtType, function(e) {
					e.stopPropagation();
					return callback($.extend(e, {svgElement: elem}));
				});
			}
		});
		
		this._elemHandlers[evtType] = this._elemHandlers[evtType] || callback; 
	}
	
	
	// class methods
	Layer.prototype = {
		declaredClass: "tmds.layers.Layer",
		'loadData' : loadData,
		'addElement' : addElement,
		'removeElement' : removeElement,
		'show' : show,
		'hide' : hide,
		'getSchematic' : getSchematic,
		'setSchematic' : setSchematic,
		'getNode' : getNode,
		'setNode' : setNode,
		'redraw' : redraw,
		'setOpacity' : setOpacity,
		'clear' : clear,
		'selectElements' : selectElements,
		'trigger' : trigger,
		'on' : on
	};
	
	return Layer;	
});